import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Play, Apple, Sparkles, ArrowDown } from "lucide-react";
import { LogoMark } from "./LogoMark";

const easeOut = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: easeOut }}
    >
      {children}
    </motion.span>
  </span>
);

const chartPath = "M0 92 C 30 78, 48 96, 78 66 S 128 34, 158 58 S 228 18, 258 40 S 300 22, 320 26";

const DashboardCard = () => (
  <div
    className="glass-panel w-full max-w-md rounded-3xl p-6 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)]"
    data-testid="hero-dashboard-card"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <LogoMark size={30} />
        <span className="font-serif-display text-lg text-slate-100">Neyrein</span>
      </div>
      <span className="flex items-center gap-2 text-xs text-sky-300/80">
        <span className="animate-pulse-dot h-2 w-2 rounded-full bg-sky-400" />
        Live sync
      </span>
    </div>
    <p className="mt-7 text-xs uppercase tracking-[0.25em] text-slate-500">Total balance · July</p>
    <p className="font-mono-num mt-1 text-4xl font-medium text-slate-50" data-testid="hero-balance">
      $4,820<span className="text-slate-500">.50</span>
    </p>
    <svg viewBox="0 0 320 120" className="mt-5 w-full" data-testid="hero-balance-chart">
      <defs>
        <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${chartPath} L 320 120 L 0 120 Z`}
        fill="url(#area)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.6 }}
      />
      <motion.path
        d={chartPath}
        fill="none"
        stroke="#38BDF8"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, delay: 1.2, ease: easeOut }}
        style={{ filter: "drop-shadow(0 0 10px rgba(56,189,248,0.6))" }}
      />
    </svg>
    <div className="mt-4 space-y-2.5">
      {[
        ["Salary", "+$3,200.00", "text-emerald-400"],
        ["Groceries", "-$84.20", "text-slate-300"],
        ["Coffee", "-$6.40", "text-slate-300"],
      ].map(([label, amount, color]) => (
        <div
          key={label}
          className="flex items-center justify-between rounded-xl border border-sky-400/10 bg-[#0F1C36]/60 px-4 py-2.5"
        >
          <span className="text-sm text-slate-400">{label}</span>
          <span className={`font-mono-num text-sm ${color}`}>{amount}</span>
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-sky-400/20 bg-sky-400/5 px-4 py-3">
      <Sparkles size={15} className="mt-0.5 shrink-0 text-sky-300" />
      <p className="text-xs leading-relaxed text-sky-200/80">
        AI insight — dining spend is up 12% this month. A small pause on takeout keeps you on target.
      </p>
    </div>
  </div>
);

export const Hero = ({ onNotify }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
  const chipX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const chipY = useTransform(sy, [-0.5, 0.5], [-12, 12]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="overview"
      onMouseMove={onMouseMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/4 h-[34rem] w-[34rem] rounded-full bg-sky-500/15 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-blue-600/15 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(56,189,248,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: easeOut }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-sky-400/20 bg-sky-400/5 px-4 py-1.5"
            data-testid="hero-eyebrow"
          >
            <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-sky-400" />
            <span className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-sky-300">
              Smart expense tracking
            </span>
          </motion.div>

          <h1
            className="font-serif-display text-5xl leading-[1.06] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            <MaskedLine delay={0.45}>Binding your life</MaskedLine>
            <MaskedLine delay={0.58}>
              to <em className="text-gradient-sky not-italic font-serif-display italic">calmer</em> money
            </MaskedLine>
            <MaskedLine delay={0.71}>habits.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: easeOut }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-description"
          >
            Neyrein records every expense and income the moment it happens, then turns the noise into
            clear reports and statistics — with optional AI assistance when you want a second pair of
            eyes on your spending.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: easeOut }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="store-playstore-btn"
              onClick={() => onNotify("Coming soon", "Neyrein will be available on Google Play. We'll open downloads shortly.")}
              className="group flex items-center gap-3 rounded-2xl border border-sky-400/25 bg-[#132342]/70 px-6 py-3.5 transition-all duration-300 hover:-translate-y-0.5 hover:border-sky-400/60 hover:shadow-[0_16px_40px_-12px_rgba(56,189,248,0.45)]"
            >
              <Play size={22} className="fill-sky-300 text-sky-300" />
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500">Get it on</span>
                <span className="block text-sm font-semibold text-slate-100">Google Play</span>
              </span>
            </button>
            <button
              data-testid="store-appstore-btn"
              onClick={() => onNotify("Coming soon", "Neyrein will be available on the App Store. We'll open downloads shortly.")}
              className="group flex items-center gap-3 rounded-2xl border border-sky-400/25 bg-[#132342]/70 px-6 py-3.5 transition-all duration-300 hover:-translate-y-0.5 hover:border-sky-400/60 hover:shadow-[0_16px_40px_-12px_rgba(56,189,248,0.45)]"
            >
              <Apple size={22} className="fill-sky-300 text-sky-300" />
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500">Download on the</span>
                <span className="block text-sm font-semibold text-slate-100">App Store</span>
              </span>
            </button>
            <span className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-600">
              + Desktop · Win / Mac / Linux
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: easeOut }}
          className="relative mx-auto hidden w-full max-w-md sm:block"
          style={{ perspective: 1200 }}
        >
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
            <DashboardCard />
          </motion.div>
          <motion.div
            style={{ x: chipX, y: chipY }}
            className="animate-float-slow glass-panel absolute -top-12 -left-6 z-10 rounded-2xl px-4 py-3"
            data-testid="hero-float-chip"
          >
            <p className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Saved this month</p>
            <p className="font-mono-num text-lg text-emerald-400">+$612.00</p>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#features"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-slate-600 transition-colors hover:text-sky-300 lg:block"
        data-testid="hero-scroll-hint"
        aria-label="Scroll to features"
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.a>
    </section>
  );
};
